import "./PostsContainer.scss"

import React from "react"

import { Post, Tag, CurrentUser } from "@fider/models"
import { Loader, Input, Icon } from "@fider/components"
import { subscribeToEvents } from "@fider/services/realtime"
import { actions, navigator, querystring } from "@fider/services"
import IconSearch from "@fider/assets/images/heroicons-search.svg"
import IconX from "@fider/assets/images/heroicons-x.svg"
import { PostFilter } from "./PostFilter"
import { ListPosts } from "./ListPosts"
import { i18n } from "@lingui/core"
import { Trans } from "@lingui/react/macro"
import { PostsSort } from "./PostsSort"

interface PostsContainerProps {
  user?: CurrentUser
  posts: Post[]
  tags: Tag[]
  countPerStatus: { [key: string]: number }
  onPostClick?: (postNumber: number, slug: string) => void
  // Tùy biến KHCN: "minimal" chỉ hiện Bộ lọc + chip từ khoá (tab hạng mục, sắp xếp, tìm kiếm do trang chủ đảm nhiệm)
  controls?: "full" | "minimal"
  // Nội dung chèn trước Bộ lọc trong thanh công cụ (ví dụ: dải nút sắp xếp)
  leading?: React.ReactNode
}

interface PostsContainerState {
  loading: boolean
  posts?: Post[] // All posts
  view: string
  filterState: FilterState // Filter state
  query: string // Seach query
  limit?: number // Limit
}

export interface FilterState {
  tags: string[]
  statuses: string[]
  myVotes: boolean
  myPosts: boolean
  noTags: boolean
}

export class PostsContainer extends React.Component<PostsContainerProps, PostsContainerState> {
  constructor(props: PostsContainerProps) {
    super(props)

    const view = querystring.get("view")

    this.state = {
      posts: this.props.posts,
      loading: false,
      view,
      query: querystring.get("query"),
      filterState: {
        tags: querystring.getArray("tags"),
        statuses: querystring.getArray("statuses"),
        myVotes: querystring.get("myvotes") === "true",
        myPosts: querystring.get("myposts") === "true",
        noTags: querystring.get("notags") === "true",
      },
      limit: querystring.getNumber("limit"),
    }
  }

  private changeFilterCriteria<K extends keyof PostsContainerState>(obj: Pick<PostsContainerState, K>): void {
    this.setState(obj, () => {
      const query = this.state.query.trim().toLowerCase()
      navigator.replaceState(
        querystring.stringify({
          statuses: this.state.filterState.statuses,
          tags: this.state.filterState.tags,
          myvotes: this.state.filterState.myVotes ? "true" : undefined,
          myposts: this.state.filterState.myPosts ? "true" : undefined,
          notags: this.state.filterState.noTags ? "true" : undefined,
          query,
          view: this.state.view,
          limit: this.state.limit,
        })
      )

      this.searchPosts(
        query,
        this.state.view || "trending",
        this.state.limit,
        this.state.filterState.tags,
        this.state.filterState.statuses,
        this.state.filterState.myVotes,
        this.state.filterState.myPosts,
        this.state.filterState.noTags
      )
    })
  }

  private timer?: number
  private async searchPosts(
    query: string,
    view: string,
    limit: number | undefined,
    tags: string[],
    statuses: string[],
    myVotes: boolean,
    myPosts: boolean,
    noTags: boolean
  ) {
    window.clearTimeout(this.timer)
    // Giữ danh sách cũ trong lúc tải để màn hình không bị trống rồi nhảy lại (tùy biến KHCN)
    this.setState({ posts: this.state.posts, loading: true })
    this.timer = window.setTimeout(() => {
      // Check if "pending" is in the statuses
      const hasPending = statuses.includes("pending")
      // Filter out "pending" from actual statuses to send to API
      const actualStatuses = statuses.filter((s) => s !== "pending")
      // Determine moderation filter
      let moderation = ""
      if (hasPending) {
        moderation = "pending"
      }

      actions
        .searchPosts({ query, view: view, limit, tags, statuses: actualStatuses, myVotes, myPosts, noTags, moderation })
        .then((response) => {
          if (response.ok && this.state.loading) {
            this.setState({ loading: false, posts: response.data })
          } else if (!response.ok) {
            this.setState({ loading: false })
          }
        })
        .catch(() => {
          // Mất mạng hoặc server đang khởi động lại: giữ danh sách cũ, không để lỗi nổi lên console
          this.setState({ loading: false })
        })
    }, 500)
  }

  // Tùy biến KHCN: chuyển tab hạng mục trên trang chủ. slug = null nghĩa là "tất cả";
  // latest = true hiển thị 10 bài mới nhất thuộc mọi hạng mục.
  private unsubscribeRealtime?: () => void
  private realtimeTimers: { [postNumber: number]: number } = {}

  // Tùy biến KHCN: bình luận / bình chọn mới ở bài đang hiển thị thì cập nhật thẻ đó (gộp trong 1 giây)
  public componentDidMount() {
    this.unsubscribeRealtime = subscribeToEvents(undefined, (event) => {
      if (event.type === "typing" || !this.state.posts?.some((p) => p.number === event.postNumber)) {
        return
      }
      window.clearTimeout(this.realtimeTimers[event.postNumber])
      this.realtimeTimers[event.postNumber] = window.setTimeout(() => void this.updateSinglePost(event.postNumber), 1000)
    })

    // Tự tải thêm khi cuộn tới gần cuối danh sách (xem loadMore)
    if (typeof IntersectionObserver !== "undefined" && this.sentinel.current) {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting) || this.state.loading || !this.getShowMoreLink()) {
            return
          }
          if (this.autoLoads >= PostsContainer.AUTO_LOADS) {
            return
          }
          this.autoLoads++
          this.loadMore()
        },
        { rootMargin: "600px 0px" }
      )
      this.observer.observe(this.sentinel.current)
    }
  }

  public componentWillUnmount() {
    this.unsubscribeRealtime?.()
    Object.values(this.realtimeTimers).forEach((timer) => window.clearTimeout(timer))
    this.observer?.disconnect()
  }

  public applyCategory = (slug: string | null, latest = false) => {
    const filterState: FilterState = { ...this.state.filterState, tags: slug ? [slug] : [], noTags: false }
    this.changeFilterCriteria({
      filterState,
      view: latest ? "recent" : this.state.view,
      limit: latest ? 10 : undefined,
    })
  }

  // Tùy biến KHCN: tìm kiếm từ ô trong hero banner
  public applySearch = (query: string) => {
    this.changeFilterCriteria({ query })
  }

  // Tùy biến KHCN: đổi cách sắp xếp từ dải nút trên trang chủ
  public applySort = (view: string) => {
    this.changeFilterCriteria({ view })
  }

  public updateSinglePost = async (postNumber: number) => {
    // Fetch the updated post and replace it in the array without changing order
    const response = await actions.getPost(postNumber)
    if (response.ok && this.state.posts) {
      const updatedPosts = this.state.posts.map((post) => (post.number === postNumber ? response.data : post))
      this.setState({ posts: updatedPosts })
    }
  }

  private handleFilterChanged = (filterState: FilterState) => {
    this.changeFilterCriteria({ filterState })
  }

  private handleSearchFilterChanged = (query: string) => {
    this.changeFilterCriteria({ query })
  }

  private handleSortChanged = (view: string) => {
    this.changeFilterCriteria({ view })
  }

  private clearSearch = () => {
    this.changeFilterCriteria({ query: "" })
  }

  // Tùy biến KHCN: tải thêm theo trang 20 bài. Cuộn tới gần cuối danh sách thì tự tải (tối đa 5 lần
  // liên tiếp để không kéo vô tận, chân trang vẫn với tới được), sau đó hiện nút "Xem thêm".
  private static readonly PAGE = 20
  private static readonly AUTO_LOADS = 5
  private autoLoads = 0
  private sentinel = React.createRef<HTMLDivElement>()
  private observer?: IntersectionObserver

  private loadMore = (): void => {
    this.changeFilterCriteria({ limit: (this.state.limit || 30) + PostsContainer.PAGE })
  }

  private showMore = (event: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>): void => {
    event.preventDefault()
    this.autoLoads = 0
    this.loadMore()
  }

  private getShowMoreLink = (): string | undefined => {
    if (this.state.posts && this.state.posts.length >= (this.state.limit || 30)) {
      return querystring.set("limit", (this.state.limit || 30) + PostsContainer.PAGE)
    }
  }

  public render() {
    const showMoreLink = this.getShowMoreLink()
    const headerClass = this.state.query ? "c-posts-container__header c-posts-container__header--searching" : "c-posts-container__header"

    return (
      <div className="c-posts-container">
        {this.props.controls === "minimal" ? (
          <div className="c-posts-container__toolbar">
            {this.props.leading}
            <PostFilter
              tags={this.props.tags}
              activeFilter={this.state.filterState}
              filtersChanged={this.handleFilterChanged}
              countPerStatus={this.props.countPerStatus}
            />
            {this.state.query && (
              <button
                type="button"
                className="c-posts-container__querychip"
                onClick={this.clearSearch}
                title={i18n._({ id: "khcn.list.clearsearch", message: "Clear search" })}
              >
                <Icon sprite={IconSearch} />
                <span>“{this.state.query}”</span>
                <Icon sprite={IconX} />
              </button>
            )}
          </div>
        ) : (
          <div className={headerClass}>
            <div className="c-posts-container__filter-col">
              <PostFilter
                tags={this.props.tags}
                activeFilter={this.state.filterState}
                filtersChanged={this.handleFilterChanged}
                countPerStatus={this.props.countPerStatus}
              />
              {!this.state.query && <PostsSort onChange={this.handleSortChanged} value={this.state.view} />}
            </div>
            <div className="c-posts-container__search-col">
              <Input
                field="query"
                icon={this.state.query ? IconX : IconSearch}
                onIconClick={this.state.query ? this.clearSearch : undefined}
                placeholder={i18n._({ id: "home.postscontainer.query.placeholder", message: "Search" })}
                value={this.state.query}
                onChange={this.handleSearchFilterChanged}
              />
            </div>
          </div>
        )}
        <div
          className={this.state.loading ? "c-posts-container__list c-posts-container__list--loading" : "c-posts-container__list"}
          aria-busy={this.state.loading}
        >
          <ListPosts
            posts={this.state.posts}
            tags={this.props.tags}
            emptyText={i18n._({ id: "home.postscontainer.label.noresults", message: "No results matched your search, try something different." })}
            onPostClick={this.props.onPostClick}
          />
          {this.state.loading && <Loader />}
          <div ref={this.sentinel} aria-hidden="true" />
          {showMoreLink && (
            <div className="my-4 text-center">
              <a href={showMoreLink} className="text-primary-base text-medium hover:underline" onClick={this.showMore}>
                <Trans id="home.postscontainer.label.viewmore">View more posts</Trans>
              </a>
            </div>
          )}
        </div>
      </div>
    )
  }
}
